import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  bookingAPI,
  canProceedWithBooking,
  validateBookingData,
  handleAPIError,
  processPaymentSuccess,
  sessionAPI
} from '../services/api';
import { tokenManager } from '../services/tokenManager';
import { paymentUtils } from '../utils/paymentUtils';
import moment from 'moment';
import { Brain } from 'lucide-react';



const SuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bookingData, setBookingData] = useState(null);
  const [sessionData, setSessionData] = useState(null);

  useEffect(() => {
    const fetchSessionById = async () => {
      try {
        setLoading(true);
        setError('');

        // Get session ID from URL parameters
        const sessionId = searchParams.get('session_id');
        console.log('Session ID from URL:', sessionId);

        if (!sessionId) {
          throw new Error('No session ID found in URL');
        }

        // Fetch session by ID from API
        const response = await bookingAPI.getSessionById(sessionId);
        console.log('Session response:', response);

        if (response && response.session) {
          const session = response.session;
          // Store the complete session data including exact times
          setSessionData(session);

          // Extract booking data from session
          setBookingData({
            doctorName: session.doctor?.name,
            date: session.date,
            startTime: session.startTime,
            endTime: session.endTime,
            doctorId: session.doctor?.id,
            patientId: session.patient?.id,
          });

          // Clear the payment flag since booking is successful
          paymentUtils.clearPaymentStatus();
        } else {
          throw new Error('Invalid response format from server');
        }
      } catch (err) {
        console.error('Error fetching session:', err);
        setError(err.message || 'Failed to fetch session details');
      } finally {
        setLoading(false);
      }
    };

    fetchSessionById();
  }, [searchParams, navigate]);

  // Helper function to construct full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return 'https://i.pravatar.cc/150?img=1';
    if (imagePath.startsWith('http')) return imagePath;

    const baseUrl = process.env.REACT_APP_BASE_URL || 'https://backend.adhdrapidcare.com';
    return imagePath.startsWith('/') ? `${baseUrl}${imagePath}` : `${baseUrl}/${imagePath}`;
  };


  const appointmentDetails = {
    doctor: {
      name: bookingData?.doctorName || sessionData?.doctor?.name || 'Unknown Provider',
      specialization: sessionData?.doctor?.role === 'nurse' ? 'Nurse' : 'Psychiatrist, MBBS, MD',
      image: getImageUrl(sessionData?.doctor?.profileImage),
    },
    date: bookingData?.date || sessionData?.date,
    formattedDate: new Date(bookingData?.date || sessionData?.date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }),
    // Use the exact session times from the API response
    time: sessionData ? `${sessionData.startTime} - ${sessionData.endTime}` : '',
    location: 'Online Session (Zoom)',
    duration: '20 minutes',
    sessionId: sessionData?.id,
    isNurseSession: sessionData?.doctor?.role === 'nurse',
    sessionMode:sessionData?.sessionMode
  };

  

  // Update the Google Calendar link generation to use exact times
  const generateGoogleCalendarLink = () => {
    if (!appointmentDetails) return '#';

    const title = encodeURIComponent(`Session with ${appointmentDetails.doctor.name}`);
    const location = encodeURIComponent("Zoom Meeting");
    const details = encodeURIComponent("Please complete the assessment before your session.");

    // Defensive: check for valid date/time values
    if (!appointmentDetails.date || !bookingData?.startTime || !bookingData?.endTime) {
      alert('Invalid session date/time. Cannot generate calendar link.');
      return '#';
    }

    // Use exact start and end times
    const startDateTime = moment(`${appointmentDetails.date} ${bookingData.startTime}`, 'YYYY-MM-DD h:mm A');
    const endDateTime = moment(`${appointmentDetails.date} ${bookingData.endTime}`, 'YYYY-MM-DD h:mm A');

    // Defensive: check for valid Date objects
    if (!startDateTime.isValid() || !endDateTime.isValid()) {
      alert('Invalid session date/time. Cannot generate calendar link.');
      return '#';
    }

    const start = startDateTime.toISOString().replace(/[-:]/g, "").replace(".000Z", "Z");
    const end = endDateTime.toISOString().replace(/[-:]/g, "").replace(".000Z", "Z");

    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${details}&location=${location}&sf=true&output=xml`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50 font-sans">
        <main className="flex-grow flex flex-col items-center justify-center px-4 py-10">
          <div className="w-full max-w-xl">
            {/* Header Skeleton */}
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-200 rounded-full mb-4 animate-pulse"></div>
              <div className="h-6 bg-gray-200 rounded animate-pulse w-48 mx-auto mb-2"></div>
              <div className="h-4 bg-gray-200 rounded animate-pulse w-64 mx-auto"></div>
            </div>

            {/* Appointment Card Skeleton */}
            <div className="bg-white rounded-2xl shadow-md p-10 border border-gray-100 mb-6">
              {/* Doctor Info Skeleton */}
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gray-200 rounded-full animate-pulse"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-32 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded animate-pulse w-40"></div>
                </div>
              </div>

              {/* Details Grid Skeleton */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 mb-6">
                {[1, 2, 3, 4].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-4 h-4 bg-gray-200 rounded animate-pulse mt-1"></div>
                    <div className="flex-1">
                      <div className="h-3 bg-gray-200 rounded animate-pulse w-16 mb-1"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Join Button Skeleton */}
              <div className="w-full h-12 bg-gray-200 rounded-lg mb-2 animate-pulse"></div>
              <div className="h-3 bg-gray-200 rounded animate-pulse w-48 mx-auto mb-5"></div>

              {/* Calendar + Dashboard Buttons Skeleton */}
              <div className="grid grid-cols-2 gap-3">
                <div className="h-10 bg-gray-200 rounded-md animate-pulse"></div>
                <div className="h-10 bg-gray-200 rounded-md animate-pulse"></div>
              </div>
            </div>

            {/* Important Info Skeleton */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-5 h-5 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded animate-pulse w-32"></div>
              </div>
              <div className="space-y-3">
                {[1, 2, 3].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-gray-200 rounded-full animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded animate-pulse flex-1"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Booking Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/doctors')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!sessionData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Invalid booking data</p>
          <button
            onClick={() => navigate('/doctors')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const importantInfo = [
    "You'll receive a reminder 10 minutes before your session",
   
    'Need help? Contact support at admin@adhdrapidcare.com',
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 font-sans">
      <main className="flex-grow flex flex-col items-center justify-center px-4 py-1">
        <div className="w-full max-w-xl">
          {/* Header */}
          <div className="text-center flex flex-col items-center py-12">

            <div className="flex items-center space-x-2 mb-2">
              <Brain className="h-8 w-8 text-blue-600"  />
              <span className="text-2xl font-bold">
                <span className="text-blue-600 opacity-60" >ADHD</span>
                <span className="text-blue-600">RapidCare</span>
              </span>
            </div>


            <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-500 rounded-full mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h2 className="text-2xl font-semibold text-blue-600 mb-1">
              {searchParams.get('stripe_session_id') ? 'Payment Successful!' : 'Booking Confirmed!'}
            </h2>
            <p className="text-gray-500 text-sm">Your {appointmentDetails.isNurseSession ? 'nurse' : 'doctor'} session has been booked and confirmed</p>
          </div>

          {/* Appointment Card */}
          <div className="bg-white rounded-2xl shadow-md p-10 border border-gray-100 mb-6">
            {/* Provider Info */}
            <div className="flex items-center gap-4 mb-6">
              <img src={appointmentDetails.doctor.image} alt={appointmentDetails.doctor.name} className="w-12 h-12 rounded-full" />
              <div>
                <h2 className="text-base font-semibold text-gray-900">{appointmentDetails.doctor.name}</h2>
                <p className="text-sm text-gray-500">{appointmentDetails.doctor.specialization}</p>
              </div>
            </div>

            {/* Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 text-sm text-gray-700 mb-6">
              <DetailItem icon="calendar" label="Date" value={appointmentDetails.formattedDate} />
              <DetailItem icon="time" label="Time" value={appointmentDetails.time} />
              <DetailItem icon="location" label="Location" value={appointmentDetails.location} />
              <DetailItem icon="duration" label="Duration" value={appointmentDetails.duration} />
              <DetailItem icon="mode" label="Mode" value={appointmentDetails.sessionMode} />
            </div>

            <button
              className="w-full bg-blue-300 text-white font-semibold py-3 rounded-lg mb-2 cursor-not-allowed opacity-50"
              disabled
            >
              Join {appointmentDetails.isNurseSession ? 'Video Call' : 'Zoom Meeting'}
            </button>
            <p className="text-center text-xs text-gray-400 mb-5">
              Link will be available 10 minutes before session
            </p>

            {/* Calendar + Dashboard Buttons */}
            <div className="grid grid-cols-1 gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="inline-flex items-center justify-center px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-all"
              >
                Go to Your Dashboard
              </button>
            </div>
          </div>

          {/* Important Info */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
              <h3 className="text-sm font-semibold text-gray-900">Important Information</h3>
                  </div>
            <ul className="space-y-3">
              {importantInfo.map((info, idx) => (
                <li key={idx} className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-green-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm text-gray-600">{info}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </main>
    </div>
  );
};

const DetailItem = ({ icon, label, value }) => {
  const icons = {
    calendar: (
      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    time: (
      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    location: (
      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
 duration: (
      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>),

    mode: (
      <svg
        className="w-4 h-4 text-gray-500"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 0 1-.825-.242m9.345-8.334a2.126 2.126 0 0 0-.476-.095 48.64 48.64 0 0 0-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0 0 11.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155"
        />
      </svg>
    )
  };

  return (
    <div className="flex items-start gap-3">
      {icons[icon]}
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="font-medium">{value}</p>
      </div>
    </div>
  );
};

export default SuccessPage;
